import React, { useState, useEffect, Fragment, useRef } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import ls from "local-storage";
import { differenceInMonths } from "date-fns";
import imageCompression from "browser-image-compression";
import { post, defaultChecks, calcCrow, usePrevious } from "./utils";
import PickerModal from "./PickerModal";
import "./form.scss";

function formatNominatimAddress(data) {
  const a = (data && data.address) || {};
  const street = a.road || a.pedestrian || a.footway || a.path || "";
  const number = a.house_number || "";
  const city =
    a.city || a.town || a.village || a.municipality || a.suburb || "";

  const streetPart = [street, number].filter(Boolean).join(" ");
  const formatted = [streetPart, city].filter(Boolean).join(", ");

  return formatted || data.display_name || "";
}

function Form({ username, userToken, onLogout }) {
  const [authorities, setAuthorities] = useState([]);
  const [selectedAuthority, setSelectedAuthority] = useState(-1);
  const [gardens, setGardens] = useState([]);
  const [selectedGarden, setSelectedGarden] = useState(-1);
  const [checks, setChecks] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingImages, setLoadingImages] = useState(false);
  const [hiddenGardens, setHiddenGardens] = useState(0);

  const [lastCoords, setLastCoords] = useState(false);
  const prevCoords = usePrevious(lastCoords);
  const [formattedAddress, setFormattedAddress] = useState(false);

  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [addressDraft, setAddressDraft] = useState("");
  const [savingAddress, setSavingAddress] = useState(false);
  const [fetchingGps, setFetchingGps] = useState(false);

  const fileInputRef = useRef();

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.watchPosition(async (position) => {
        setLastCoords(position.coords);
      });
    }
  }, []);

  useEffect(() => {
    async function getLocation() {
      const { data } = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&addressdetails=1&lat=${lastCoords.latitude}&lon=${lastCoords.longitude}&accept-language=he`
      );

      const addr = formatNominatimAddress(data);
      if (addr) {
        setFormattedAddress(addr);
      } else {
        console.warn("Geocode background failed:", data && data.error);
      }
    }

    if (!prevCoords && lastCoords) {
      getLocation();
      return;
    }

    if (!prevCoords || !lastCoords) {
      return;
    }

    const length = calcCrow(
      prevCoords.latitude,
      prevCoords.longitude,
      lastCoords.latitude,
      lastCoords.longitude
    );

    if (length >= 0.05) {
      getLocation();
    }
  }, [prevCoords, lastCoords]);

  useEffect(() => {
    async function init() {
      if (username && userToken) {
        let data = await post("/authorities/get", {
          user: username,
          token: userToken,
        });

        if (data.result === "error") {
          alert("שגיאה - יש להתחבר מחדש");
          onLogout();
          return;
        }

        const curDate = new Date();
        let hidden = 0;

        for (let i = data.authorities.length - 1; i >= 0; i--) {
          for (let j = data.authorities[i].gardens.length - 1; j >= 0; j--) {
            const val = ls(
              `hide_${data.authorities[i].name}_${data.authorities[i].gardens[j].name}`
            );
            if (val && differenceInMonths(curDate, new Date(val)) < 1) {
              data.authorities[i].gardens.splice(j, 1);
              hidden++;
            }
          }
        }

        setHiddenGardens(hidden);

        setAuthorities([...data.authorities]);
        setLoading(false);
      }
    }

    init();
  }, [username, userToken, onLogout]);

  useEffect(() => {
    if (selectedAuthority > -1) {
      setGardens(authorities[selectedAuthority].gardens);
      setSelectedGarden(-1);
    }
  }, [selectedAuthority, authorities]);

  useEffect(() => {
    if (selectedAuthority > -1 && selectedGarden > -1) {
      const checks =
        authorities[selectedAuthority].gardens[selectedGarden].checks;

      if (checks.length) {
        setChecks(
          checks.map((c) => ({ name: c.name, status: true, description: "" }))
        );
      } else {
        setChecks(
          defaultChecks.map((c) => ({ name: c, status: true, description: "" }))
        );
      }

      setFacilities(
        authorities[selectedAuthority].gardens[selectedGarden].facilities.map(
          (f) => ({ ...f, exists: true })
        )
      );
    }
  }, [selectedGarden, authorities, selectedAuthority]);

  function handleAuthorityChanged(value) {
    setSelectedAuthority(value);
  }

  function handleGardenChanged(value) {
    setSelectedGarden(value);
  }

  // Convert authorities and gardens to picker options format
  const authorityOptions = authorities.map((a, idx) => ({
    value: idx,
    label: a.name,
  }));

  const gardenOptions = gardens.map((g, idx) => ({
    value: idx,
    label: g.name,
  }));

  function handleOpenAddressModal() {
    const current =
      authorities[selectedAuthority].gardens[selectedGarden].address || "";
    setAddressDraft(current);
    setAddressModalOpen(true);
  }

  function handleAddressDraftChanged(e) {
    setAddressDraft(e.target.value);
  }

  async function handleFillFromGpsClicked() {
    if (formattedAddress) {
      setAddressDraft(formattedAddress);
      return;
    }

    if (!navigator.geolocation) {
      alert("המכשיר אינו תומך באיתור מיקום");
      return;
    }

    setFetchingGps(true);
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        });
      });

      const { data } = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&addressdetails=1&lat=${position.coords.latitude}&lon=${position.coords.longitude}&accept-language=he`
      );

      const addr = formatNominatimAddress(data);
      if (!addr) {
        console.warn("Geocode failed:", data && data.error);
        alert("לא נמצאה כתובת עבור המיקום הנוכחי");
        return;
      }

      setFormattedAddress(addr);
      setLastCoords(position.coords);
      setAddressDraft(addr);
    } catch (err) {
      if (err && err.code === 1) {
        alert("יש לאשר גישה למיקום בדפדפן");
      } else if (err && err.code === 3) {
        alert("איתור המיקום ארך זמן רב מדי, יש לנסות שוב");
      } else {
        alert("שגיאה באיתור המיקום");
      }
    } finally {
      setFetchingGps(false);
    }
  }

  function handleCancelAddressClicked() {
    setAddressModalOpen(false);
    setAddressDraft("");
  }

  async function handleSaveAddressClicked() {
    const garden = authorities[selectedAuthority].gardens[selectedGarden];
    setSavingAddress(true);
    const data = await post("/garden/updateAddress", {
      user: username,
      token: userToken,
      authority_id: authorities[selectedAuthority]._id,
      garden_id: garden._id,
      address: addressDraft,
    });
    setSavingAddress(false);

    if (data && data.result === "success") {
      garden.address = data.address;
      setAuthorities([...authorities]);
      setAddressModalOpen(false);
      setAddressDraft("");
    } else {
      alert((data && data.message) || "שגיאה בשמירה");
    }
  }

  function handleStatusClicked(idx, status) {
    checks[idx].status = status;
    setChecks([...checks]);
  }

  function handleDescriptionChanged(idx, e) {
    checks[idx].description = e.target.value;
    setChecks([...checks]);
  }

  function handleExistsClicked(facility) {
    facility.exists = true;
    setFacilities([...facilities]);
  }

  function handleNotExistsClicked(facility) {
    facility.exists = false;
    setFacilities([...facilities]);
  }

  async function handleSendFormClicked() {
    ls(
      `hide_${authorities[selectedAuthority].name}_${authorities[selectedAuthority].gardens[selectedGarden].name}`,
      +new Date()
    );

    setLoading(true);

    let data;
    if (username === "גידי") {
      data = { result: "success" };
    } else {
      data = await post("/garden/createReport", {
        user: username,
        token: userToken,
        authority: authorities[selectedAuthority].name,
        garden: authorities[selectedAuthority].gardens[selectedGarden].name,
        checks,
        facilities,
        bedding_type:
          authorities[selectedAuthority].gardens[selectedGarden].bedding_type,
        images,
        latitude: lastCoords.latitude,
        longitude: lastCoords.longitude,
        formattedAddress,
      });
    }

    if (data.result === "error") {
      alert("לא ניתן לשלוח את הדוח לפני שהוגדר סוג מצע");
      setLoading(false);
      return;
    }

    alert("הדוח נשלח בהצלחה!");
    setLoading(false);

    authorities[selectedAuthority].gardens.splice(selectedGarden, 1);
    setAuthorities([...authorities]);

    setSelectedGarden(-1);
    setImages([]);
  }

  function handleShowGardensClicked() {
    if (!window.confirm("להציג את כל הגנים המוסתרים?")) {
      return;
    }

    for (const key in localStorage) {
      if (key.indexOf("hide_") > -1) {
        ls.remove(key);
      }
    }

    window.location.reload();
  }

  function handleTakePictureClicked() {
    fileInputRef.current.click();
  }

  function loadImage(file) {
    return new Promise((resolve) => {
      const fr = new FileReader();

      fr.onload = () => {
        resolve(fr.result);
      };

      fr.readAsDataURL(file);
    });
  }

  async function handleFileInputChanged() {
    setLoadingImages(true);
    const newImages = [...images];

    for (let i = 0; i < fileInputRef.current.files.length; i++) {
      const compressedFile = await imageCompression(
        fileInputRef.current.files[i],
        {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        }
      );
      const data = await loadImage(compressedFile);
      newImages.push(data);
    }

    setImages(newImages);
    setLoadingImages(false);
  }

  function handleRemoveImageClicked(idx) {
    if (window.confirm("להסיר את התמונה שנלחצה?")) {
      images.splice(idx, 1);
      setImages([...images]);
    }
  }

  if (loading) {
    return <div>טוען...</div>;
  }

  if (!authorities.length) {
    return (
      <div>לא קיימות רשויות - יש לבקש ממנהל המערכת להוסיף רשויות תחילה</div>
    );
  }

  return (
    <div className="form-wrapper">
      {hiddenGardens > 0 && (
        <div>
          <div>
            שימו לב שיש {hiddenGardens} גנים מוסתרים.
            <br />
            <a href="#" onClick={handleShowGardensClicked}>
              לחץ כדי להציג
            </a>
          </div>
          <br />
        </div>
      )}
      <div className="picker-wrapper">
        <PickerModal
          options={authorityOptions}
          value={selectedAuthority}
          onChange={handleAuthorityChanged}
          placeholder="בחר רשות..."
          title="בחירת רשות"
          searchPlaceholder="חיפוש רשות..."
        />
      </div>
      <br />
      {selectedAuthority > -1 && (
        <div className="picker-wrapper">
          <PickerModal
            options={gardenOptions}
            value={selectedGarden}
            onChange={handleGardenChanged}
            placeholder="בחר גן..."
            title="בחירת גן"
            searchPlaceholder="חיפוש גן..."
          />
        </div>
      )}
      {selectedGarden > -1 && (
        <Fragment>
          {authorities[selectedAuthority].gardens[selectedGarden].address ? (
            <Fragment>
              <div className="address-line">
                <span className="bold">כתובת: </span>
                {
                  authorities[selectedAuthority].gardens[selectedGarden]
                    .address
                }
                <button
                  type="button"
                  className="address-edit-btn"
                  onClick={handleOpenAddressModal}
                  aria-label="ערוך כתובת"
                >
                  ✎
                </button>
              </div>
              <div className="nav-icons">
                <a
                  href={
                    "https://www.waze.com/ul?q=" +
                    encodeURIComponent(
                      authorities[selectedAuthority].gardens[selectedGarden]
                        .address
                    ) +
                    "&navigate=yes"
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="ניווט ב-Waze"
                >
                  <img src="/waze.svg" alt="Waze" />
                </a>
                <a
                  href={
                    "https://www.google.com/maps/dir/?api=1&destination=" +
                    encodeURIComponent(
                      authorities[selectedAuthority].gardens[selectedGarden]
                        .address
                    )
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="ניווט ב-Google Maps"
                >
                  <img src="/google-maps.svg" alt="Google Maps" />
                </a>
              </div>
              <br />
            </Fragment>
          ) : (
            <Fragment>
              <div className="address-line">
                <button
                  type="button"
                  className="address-add-btn"
                  onClick={handleOpenAddressModal}
                >
                  + הוסף כתובת
                </button>
              </div>
              <br />
            </Fragment>
          )}
          {addressModalOpen && (
            <div className="address-modal-overlay">
              <div className="address-modal">
                <h3>
                  {authorities[selectedAuthority].gardens[selectedGarden]
                    .address
                    ? "עריכת כתובת"
                    : "הוספת כתובת"}
                </h3>
                <input
                  type="text"
                  value={addressDraft}
                  onChange={handleAddressDraftChanged}
                  placeholder="כתובת..."
                />
                <button
                  type="button"
                  className="address-gps-btn"
                  onClick={handleFillFromGpsClicked}
                  disabled={savingAddress || fetchingGps}
                >
                  📍 {fetchingGps ? "מאתר מיקום..." : "מילוי מ-GPS"}
                </button>
                <div className="address-modal-actions">
                  <button
                    type="button"
                    onClick={handleSaveAddressClicked}
                    disabled={savingAddress}
                  >
                    {savingAddress ? "שומר..." : "שמור"}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelAddressClicked}
                    disabled={savingAddress}
                  >
                    ביטול
                  </button>
                </div>
              </div>
            </div>
          )}
          <div>
            <span className="bold">סוג מצע: </span>
            {
              authorities[selectedAuthority].gardens[selectedGarden]
                .bedding_type
            }
          </div>
          <br />
          <div className="checks">
            {checks.map((c, idx) => (
              <div key={idx}>
                <div className="bold">{c.name}</div>
                <div className="status">
                  <img
                    onClick={() => handleStatusClicked(idx, true)}
                    src="/check.jpg"
                    alt="V"
                    className={`${c.status === true ? "checked" : ""}`}
                  />
                  <img
                    onClick={() => handleStatusClicked(idx, false)}
                    src="/x.png"
                    alt="X"
                    className={`${c.status === false ? "checked" : ""}`}
                  />
                </div>
                <div className="description">
                  <textarea
                    placeholder="הערות..."
                    value={c.description}
                    onChange={(e) => handleDescriptionChanged(idx, e)}
                  ></textarea>
                </div>
                <hr />
              </div>
            ))}
          </div>
          <br />
          <h2>רשימת מתקנים:</h2>
          <div className="facilities">
            {facilities.map((f, idx) => (
              <Fragment key={idx}>
                <div className="facility">
                  <div className="bold">{f.name}</div>
                  <label>
                    <input
                      name={`${f.name}_${idx}`}
                      type="radio"
                      checked={!!f.exists}
                      onChange={() => handleExistsClicked(f)}
                    />
                    קיים
                  </label>
                  <label>
                    <input
                      name={`${f.name}_${idx}`}
                      type="radio"
                      checked={!f.exists}
                      onChange={() => handleNotExistsClicked(f)}
                    />
                    לא קיים
                  </label>
                </div>
                <hr />
              </Fragment>
            ))}
          </div>
          <br />
          <h2>תמונות:</h2>
          <div className="images-wrapper">
            {images.map((imageData, idx) => (
              <img
                key={idx}
                src={imageData}
                alt="img"
                onClick={() => handleRemoveImageClicked(idx)}
              />
            ))}
          </div>
          <div>
            {images.length > 0 && <div>כדי להסיר תמונה יש ללחוץ עליה</div>}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileInputChanged}
            />
            <button onClick={handleTakePictureClicked} disabled={loadingImages}>
              {loadingImages ? "מוסיף..." : "הוסף תמונה"}
            </button>
          </div>
          <br />
          {formattedAddress && (
            <div>
              <b>מיקום:</b> {formattedAddress}
            </div>
          )}
          <br />
          <button
            disabled={
              loading ||
              selectedAuthority === -1 ||
              selectedGarden === -1 ||
              checks.find((c) => c.status === null)
            }
            onClick={handleSendFormClicked}
          >
            שליחת טופס
          </button>
        </Fragment>
      )}
    </div>
  );
}

Form.propTypes = {
  username: PropTypes.string,
  userToken: PropTypes.string,
  onLogout: PropTypes.func,
};

export default Form;
